import { useState } from 'react';

import { Button, MenuItem, type PopoverOrigin } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { Link, type To } from 'react-router-dom';

import { IconsMenu } from '@modules/_shared/components/actions-menu/IconsMenu';

export type Actions = {
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  to?: To;
  icon?: React.ReactNode;
};
type ActionsMenuProps = {
  label: React.ReactNode;
  actions: Actions[];
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
};

export const ActionsMenu = ({
  label,
  actions,
  anchorOrigin,
  transformOrigin,
}: ActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isActionsMenuOpen = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleActionsMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        id="actions-button"
        aria-controls={isActionsMenuOpen ? 'actions-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isActionsMenuOpen ? 'true' : undefined}
        variant="outlined"
        disableElevation
        onClick={handleClick}
        endIcon={<ChevronDown />}
        color="neutral"
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          borderRadius: '8px',
          borderColor: 'divider',
          transition: 'all 0.15s ease',
          '&:hover': {
            borderColor: 'grey.400',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          },
        }}
      >
        {label}
      </Button>
      {
        <IconsMenu
          id="actions-menu"
          slotProps={{
            list: {
              'aria-labelledby': 'actions-button',
            },
          }}
          anchorEl={anchorEl}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          open={isActionsMenuOpen}
          onClose={handleActionsMenuClose}
        >
          {actions.map(action => (
            <MenuItem
              key={action.label}
              onClick={() => {
                handleActionsMenuClose();
                action.onClick?.();
              }}
              component={action.to ? Link : 'li'}
              to={action.to}
              disabled={action.disabled}
              sx={{ gap: 1.5 }}
            >
              {action.icon}
              {action.label}
            </MenuItem>
          ))}
        </IconsMenu>
      }
    </>
  );
};
